import React from 'react';

import cx from 'classnames';
import { showModal } from 'modal.react.js';
import { isNil } from 'ramda';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import RenderInDomNode from 'components/Pages/Admin/Page/common/RenderInStuff';
import { BuilderFooterButton } from 'components/Pages/Admin/PageTypes/shared/BuilderFooterButton';
import styles from 'components/shared/ActionButtons/index.module.css';
import Button from 'components/shared/Button';
import FormActions from 'components/shared/FormAdapters/FormDefault/FormActions';
import area from 'components/shared/GlobalModalArea/area';
import Share2 from 'components/shared/Icon/icons/Share2';
import Users from 'components/shared/Icon/icons/Users';
import { IconButton } from 'components/shared/IconButton';
import StickyFooter from 'components/shared/StickyFooter/StickyFooter';
import Tooltip, { VARIANTS } from 'components/shared/Tooltip';
import { ACCOUNT_TYPE_AGENT, ITINERARIES_ITEM_TYPES, LIBRARY_ITINERARIES } from 'constants/index';
import { PERMISSION_GUEST_PORTAL, useIsSubscriptionAllowed } from 'hooks/useIsSubscriptionAllowed';
import useMediaBreakpoints from 'hooks/useMediaBreakpoints';
import { useUserActivityChannel } from 'hooks/useUserActivityChannel';
import { itinerariesItemDataSelector } from 'store/itinerariesItem/selectors';
import { isMovingBlockedSelector } from 'store/movingBlocked/selectors';
import { appHasChangesSelector } from 'store/unsavedChanges/selectors';
import { confirmAlert } from 'utils/alert';
import { useItinerariesItemContext } from 'utils/itineraries/context';
import { useItinerariesItemStepPath } from 'utils/itineraries/functions/useItinerariesItemStepPath';
import { GUEST_PORTAL_STEP_LIST } from 'utils/itineraries/guestPortal/map';

import { EnterPortalModal } from '../../../../Itineraries/Table/EnterPortalModal';
import { useTimelineContext } from '../TimelineContext';
import { useActionButtons } from './functions';
import s from './index.module.css';

function ActionButtons() {
  const doesAppHaveChanges = useSelector(appHasChangesSelector);
  const itinerariesItem = useSelector(itinerariesItemDataSelector);
  const history = useHistory();
  const getGuestPortalPathStep = useItinerariesItemStepPath(ITINERARIES_ITEM_TYPES.GUEST_PORTAL);
  const { isTabletOrMobile } = useMediaBreakpoints();
  const { itemType, withMultipleUsersWarning, withTimelineBrokenWarning } = useItinerariesItemContext();
  const { isSubmitting, hasValidationErrors, handleSave, handleNext, isTripEventsChanged } = useTimelineContext();
  const { isShareEnabled, isShareVisible, handleShare } = useActionButtons();
  const isGuestPortalAllowed = useIsSubscriptionAllowed(ACCOUNT_TYPE_AGENT, PERMISSION_GUEST_PORTAL);
  const { renderUserBubbles } = useUserActivityChannel(itinerariesItem);
  const isMovingBlocked = useSelector(isMovingBlockedSelector);

  const goToGuestPortal = () => {
    if (itinerariesItem?.guestPortals.length > 1) {
      return showModal<boolean | void>(area, (res) => {
        const unregister = history.listen(() => res(false));
        return (
          <EnterPortalModal
            guestPortals={itinerariesItem.guestPortals}
            res={res}
            onEdit={() => {
              res();
              unregister();
            }}
          />
        );
      });
    }
    confirmAlert({
      title: 'You will be redirected to the Guest Portal now.',
      confirmButtonText: "Let's Go!",
      onConfirm: () => {
        history.push(
          getGuestPortalPathStep(
            itinerariesItem.guestPortalLastAvailableStep ?? GUEST_PORTAL_STEP_LIST[0],
            itinerariesItem.guestPortalId,
            {
              libType: LIBRARY_ITINERARIES,
            },
          ),
        );
      },
    });
  };

  return (
    <>
      <RenderInDomNode id="stuff" refNode={null}>
        <div className={s.container}>
          <div className={styles.divided}>
            {renderUserBubbles()}
            {isShareVisible &&
              (isTabletOrMobile ? (
                <Button
                  size="medium"
                  type="default"
                  shape="ellipse"
                  icon={Share2}
                  className={styles.button}
                  onClick={handleShare}
                  disabled={isSubmitting || hasValidationErrors || !isShareEnabled || isTripEventsChanged}
                >
                  Share
                </Button>
              ) : (
                <>
                  <div data-tip data-for="share-modal-button">
                    <IconButton
                      className={styles.share}
                      icon={Share2}
                      onClick={handleShare}
                      disabled={isSubmitting || hasValidationErrors || !isShareEnabled || isTripEventsChanged}
                    />
                    <Tooltip variant={VARIANTS.DARK} id="share-modal-button">
                      Share
                    </Tooltip>
                  </div>
                </>
              ))}

            {itemType === ITINERARIES_ITEM_TYPES.ITINERARY &&
              itinerariesItem.confirmed &&
              !isNil(itinerariesItem.guestPortalId) &&
              (isTabletOrMobile ? (
                <Button
                  className={cx(styles.button, styles.headerButton)}
                  onClick={goToGuestPortal}
                  size="medium"
                  type="default"
                  shape="ellipse"
                  icon={Users}
                  disabled={isSubmitting || doesAppHaveChanges || !isGuestPortalAllowed || isMovingBlocked}
                >
                  GP
                </Button>
              ) : (
                <IconButton
                  icon={Users}
                  className={s.icon}
                  withTooltip
                  tooltipId="go-to-guest-portal"
                  tooltipContent="Open Guest Portal"
                  onClick={goToGuestPortal}
                  disabled={isSubmitting || doesAppHaveChanges || !isGuestPortalAllowed || isMovingBlocked}
                />
              ))}
            <Button
              size="medium"
              type={isTripEventsChanged ? undefined : 'grey'}
              shape="ellipse"
              onClick={withTimelineBrokenWarning(withMultipleUsersWarning(handleSave))}
              disabled={isSubmitting || hasValidationErrors || isMovingBlocked}
              className={styles.button}
            >
              Save
            </Button>
          </div>
        </div>
      </RenderInDomNode>
      <StickyFooter className={s.footer}>
        <FormActions isRight>
          <BuilderFooterButton
            id="itineraryDetailsNextButton"
            className={s.nextButton}
            disabled={isSubmitting || hasValidationErrors || isMovingBlocked}
            onClick={withTimelineBrokenWarning(withMultipleUsersWarning(handleNext))}
          >
            Next
          </BuilderFooterButton>
        </FormActions>
      </StickyFooter>
    </>
  );
}

export default ActionButtons;
